import { DragDropList } from '@/components/ui/custom/dataInput/DragDrop';
import { Loading } from '@/components/ui/daisy';
import useCourseUnits from '@/data/hook/unit/useUnits';
import useNestedOptimisticReorder from '@/data/hook/useNestedOptimisticReorder';
import Lesson from '@/models/Lesson';
import Unit from '@/models/Unit';
import { adapterLessonBlockOrder } from '@/utils/lessonBlocks';
import { useCallback } from 'react';
import UnitCard from './UnitCard';
import UnitFormList from './UnitFormList';
import useUnitsWithLessons from '@/data/hook/unit/useUnitsWithLessons';

const EMPTY_LESSONS: Lesson[] = [];

type UnitsTabContentProps = {
  slug: string;
  courseLineId: number;
  moduleId: number;
};

export default function UnitsTabContent({
  slug,
  courseLineId,
  moduleId,
}: UnitsTabContentProps) {
  const {
    units,
    isLoading,
    createUnit,
    isCreating,
    deleteUnit,
    isDeleting,
    reorderUnits,
    isReordering,
  } = useCourseUnits({ slug });

  const { childrenMap, isLoadingLessons } = useUnitsWithLessons({
    slug,
    units,
  });

  const handleReorder = useCallback(
    (
      reorderedParents: Unit[],
      reorderedChildrenMap: Record<string, Lesson[]>,
    ) => {
      const order = adapterLessonBlockOrder(
        reorderedParents,
        reorderedChildrenMap,
      );
      reorderUnits(order);
    },
    [reorderUnits],
  );

  const {
    optimisticParents,
    optimisticChildrenMap,
    handleDragEnd,
    isDraggingParent,
    onBeforeCapture,
  } = useNestedOptimisticReorder({
    parents: units,
    childrenMap,
    parentType: 'UNIT',
    parentPrefix: 'unit',
    isReordering,
    onReorder: handleReorder,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading variant="spinner" size="lg" color="primary" />
      </div>
    );
  }

  return (
    <div className="relative">
      <DragDropList
        onDragEnd={handleDragEnd}
        onBeforeCapture={onBeforeCapture}
        groupId="units"
        type="UNIT"
        customPlaceholder
        className="gap-0"
      >
        {optimisticParents.map((unit, index) => (
          <UnitCard
            key={unit.id}
            isDraggable
            unit={unit}
            index={index}
            courseLineId={courseLineId}
            moduleId={moduleId}
            slug={slug}
            isDragging={isDraggingParent}
            lessons={optimisticChildrenMap[String(unit.id)] ?? EMPTY_LESSONS}
            isLoadingLessons={isLoadingLessons[String(unit.id)] ?? false}
            isReordering={isReordering}
            onDelete={deleteUnit}
            isDeleting={isDeleting}
          />
        ))}
      </DragDropList>

      <UnitFormList
        unitsCount={units.length}
        onSubmit={createUnit}
        isLoading={isCreating}
      />
    </div>
  );
}
